// Super Admin — Business Profiles oversight panel (PlatformAdmin tab)
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

export function BusinessProfilesPanel() {
  const [d, setD] = useState(null);
  const load = () => api.get("/platform/business-profiles").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 p-4">Loading…</div>;
  const toggleFeature = async (orgId) => {
    const { data } = await api.post(`/platform/business-profiles/enable/${orgId}`);
    toast.success(data.enabled ? "Feature enabled" : "Feature disabled");
    load();
  };
  const suspend = async (pid) => {
    const { data } = await api.post(`/platform/business-profiles/${pid}/suspend`);
    toast.success(`Profile ${data.status}`);
    load();
  };
  return (
    <div className="space-y-4" data-testid="platform-bp-panel">
      <div className="grid grid-cols-3 lg:grid-cols-7 gap-2">
        {[["Total", d.stats.total], ["Live", d.stats.live], ["Draft", d.stats.draft],
          ["New this month", d.stats.created_this_month], ["Total views", d.stats.total_views],
          ["Enquiries", d.stats.total_enquiries], ["Searches (month)", d.stats.searches_this_month]].map(([l, v]) => (
          <div key={l} className="hard-card p-3">
            <div className="font-display font-black text-xl text-[#FF5F15]">{v}</div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">{l}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <section className="hard-card p-4">
          <div className="label-tag mb-2">Top searches</div>
          {(d.top_searches || []).length === 0 && <p className="text-xs text-zinc-500">No directory searches yet.</p>}
          {(d.top_searches || []).map((t, i) => (
            <div key={i} className="flex items-center gap-2 text-sm py-1 border-b border-zinc-100 dark:border-white/[0.05]">
              <span className="font-bold">{t.service || "—"}</span>
              <span className="text-zinc-500 text-xs">{t.location || "anywhere"}</span>
              <span className="ml-auto font-mono text-xs text-[#FF5F15]">{t.count}</span>
            </div>
          ))}
        </section>
        <section className="hard-card p-4">
          <div className="label-tag mb-2">Searches with no results (supply gaps)</div>
          {(d.no_result_searches || []).length === 0 && <p className="text-xs text-zinc-500">None — every search found contractors.</p>}
          {(d.no_result_searches || []).map((t, i) => (
            <div key={i} className="flex items-center gap-2 text-sm py-1 border-b border-zinc-100 dark:border-white/[0.05]">
              <span className="font-bold text-amber-500">{t.service || "—"}</span>
              <span className="text-zinc-500 text-xs">{t.location || "anywhere"}</span>
              <span className="ml-auto font-mono text-xs">{t.count}</span>
            </div>
          ))}
        </section>
      </div>
      <section className="hard-card p-4">
        <div className="label-tag mb-2">Feature enablement (OFF by default)</div>
        <div className="space-y-1.5">
          {d.orgs.map((o) => (
            <div key={o.id} className="flex items-center gap-2 text-sm">
              <span className="font-bold">{o.name}</span>
              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${o.enabled ? "bg-emerald-500/15 text-emerald-500" : "bg-zinc-500/15 text-zinc-400"}`}>{o.enabled ? "Enabled" : "Off"}</span>
              <Button size="sm" variant="outline" className="h-6 rounded text-[9px] uppercase ml-auto" onClick={() => toggleFeature(o.id)} data-testid={`bp-feat-${o.id}`}>
                {o.enabled ? "Disable" : "Enable"}
              </Button>
            </div>
          ))}
        </div>
      </section>
      <section className="hard-card p-4">
        <div className="label-tag mb-2">Profiles</div>
        {d.profiles.length === 0 && <p className="text-xs text-zinc-500">No profiles created yet.</p>}
        {d.profiles.map((p) => (
          <div key={p.id} className="flex items-center gap-2 py-1.5 border-b border-zinc-100 dark:border-white/[0.05] text-sm flex-wrap">
            <span className="font-bold">{p.name || p.org_name}</span>
            <a href={`/company/${p.slug}`} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-[#FF5F15] hover:underline">/company/{p.slug} <ExternalLink className="w-3 h-3 inline" /></a>
            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${p.status === "live" ? "bg-emerald-500/15 text-emerald-500" : p.status === "suspended" ? "bg-red-500/15 text-red-500" : "bg-zinc-500/15 text-zinc-400"}`}>{p.status}</span>
            <Button size="sm" variant="outline" className="h-6 rounded text-[9px] uppercase ml-auto" onClick={() => suspend(p.id)} data-testid={`bp-suspend-${p.id}`}>
              {p.status === "suspended" ? "Unsuspend" : "Suspend"}
            </Button>
          </div>
        ))}
      </section>
    </div>
  );
}
